import { useState, useEffect } from 'react';
import {
  HeartPulse, Plus, Copy, Check, Code, Trash2, ArrowLeft, Loader2,
  RefreshCw, X, Shield, Activity, Users, TrendingUp, PieChart, Eye,
  Sparkles, ExternalLink, Info, BookOpen, ChevronDown, ChevronUp,
  TestTube,
} from 'lucide-react';
import FeatureGate from '../components/FeatureGate';
import {
  registerHealthToken, listHealthTokens, deleteHealthToken, getHealthScore,
} from '../services/api';
import { InfoTip } from '../components/ui';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const RATING_STYLES = {
  excellent: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', color: '#10b981' },
  good:      { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/30',    color: '#3b82f6' },
  fair:      { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30',   color: '#f59e0b' },
  poor:      { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30',     color: '#ef4444' },
  pending:   { bg: 'bg-gray-500/15',    text: 'text-gray-400',    border: 'border-gray-500/30',    color: '#6b7280' },
};

const FACTOR_ICONS = {
  volumeConsistency:  Activity,
  tradingActivity:    Users,
  buySellBalance:     TrendingUp,
  liquidityDepth:     PieChart,
  holderDistribution: Users,
  organicPatterns:    Shield,
};

// Generic, provider-agnostic source labels. The engine returns 'market-data',
// 'dex-metrics', 'on-chain', 'heuristic' (with '-unavailable' variants for
// the offline case) so users don't see our infra choices.
const SOURCE_BADGES = {
  'market-data':             { label: 'MARKET DATA', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  'dex-metrics':             { label: 'DEX METRICS', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  'on-chain':                { label: 'ON-CHAIN',    cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  'heuristic':               { label: 'HEURISTIC',   cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  'market-data-unavailable': { label: 'UNAVAILABLE', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
  'dex-metrics-unavailable': { label: 'UNAVAILABLE', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
  'on-chain-unavailable':    { label: 'UNAVAILABLE', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
  'heuristic-unavailable':   { label: 'UNAVAILABLE', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
};

const NETWORKS = [
  { id: 'ethereum',  label: 'Ethereum' },
  { id: 'base',      label: 'Base' },
  { id: 'arbitrum',  label: 'Arbitrum' },
  { id: 'optimism',  label: 'Optimism' },
  { id: 'polygon',   label: 'Polygon' },
  { id: 'bsc',       label: 'BNB Chain' },
  { id: 'avalanche', label: 'Avalanche' },
  { id: 'blast',     label: 'Blast' },
  { id: 'linea',     label: 'Linea' },
  { id: 'scroll',    label: 'Scroll' },
];

// Well-known mainnet tokens users can quick-load to see what a real health
// report looks like before they bother registering their own. Diversified
// across categories so users can see how the score behaves on stable / blue-chip
// DeFi / oracle / memecoin / wrapped / L2-native tokens.
const SAMPLE_TOKENS = [
  { symbol: 'USDC', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', network: 'ethereum', name: 'USD Coin',  category: 'Stablecoin' },
  { symbol: 'WETH', address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', network: 'ethereum', name: 'Wrapped ETH', category: 'Wrapped major' },
  { symbol: 'UNI',  address: '0x1f9840a85d5aF5bf1D1762F925BdAdcC3624a02E', network: 'ethereum', name: 'Uniswap',    category: 'DeFi blue chip' },
  { symbol: 'AAVE', address: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9', network: 'ethereum', name: 'Aave',       category: 'DeFi blue chip' },
  { symbol: 'LINK', address: '0x514910771AF9Ca656af840dff83E8264EcF986CA', network: 'ethereum', name: 'Chainlink',  category: 'Oracle infra' },
  { symbol: 'PEPE', address: '0x6982508145454Ce325dDbE47a25d4ec3d2311933', network: 'ethereum', name: 'Pepe',       category: 'Memecoin' },
];

function ScoreRing({ value, rating, size = 80 }) {
  const rs = RATING_STYLES[rating] || RATING_STYLES.pending;
  const r = (size - 8) / 2;
  const circumference = 2 * Math.PI * r;
  const isPending = rating === 'pending' || value == null;
  const dashArray = isPending ? 0 : (value / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#374151" strokeWidth="4" />
        {!isPending && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={rs.color} strokeWidth="4"
            strokeDasharray={`${dashArray} ${circumference}`} strokeLinecap="round" />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`font-bold ${isPending ? 'text-gray-500' : 'text-white'}`} style={{ fontSize: size * 0.28 }}>
          {isPending ? '—' : value}
        </span>
      </div>
    </div>
  );
}

function SourceBadge({ source }) {
  const cfg = SOURCE_BADGES[source] || { label: (source || 'unknown').toUpperCase(), cls: 'bg-dark-700 text-gray-400 border-dark-600' };
  return <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${cfg.cls}`}>{cfg.label}</span>;
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function Hero({ onRefresh, refreshing, onRegister }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-pink-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-pink-500/20 p-6">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-pink-400/10 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-pink-500/20 border border-pink-400/30 flex items-center justify-center flex-shrink-0">
            <HeartPulse className="w-6 h-6 text-pink-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Volume Health Widget</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Real on-chain</span>
            </div>
            <p className="text-xs text-pink-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Elite · Issuer Tools · Embeddable badges that score your token's organic activity from real DEX metrics, market data, and on-chain holder distribution
              <InfoTip side="bottom">6 factors, 5 ratings, auto-refresh every 5 min. NOT a legitimacy certification — measures TRADING metrics organic-ness only. Use alongside Listing Readiness for compliance angle.</InfoTip>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-pink-400/30 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 disabled:opacity-50 text-xs font-medium"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          )}
          {onRegister && (
            <button
              onClick={onRegister}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-pink-500 text-white hover:bg-pink-400 text-xs font-bold"
            >
              <Plus className="w-3.5 h-3.5" /> Register token
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── About card (collapsible explainer) ────────────────────────────────────

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-pink-500/10 border border-pink-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-pink-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
            <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">The problem</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Buyers see your token's price chart but can't tell at a glance if the volume is organic, the liquidity is real, or the holders are spread out. Founders waste time answering "is this token healthy?" a thousand times in Discord and Telegram.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">What this tool does</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Computes a <span className="text-white">0–100 health score</span> for your token from 6 real factors — volume consistency, trading activity, buy/sell balance, liquidity depth, holder distribution, organic-pattern check. Gives you an <span className="text-white">embeddable SVG badge</span> you can paste anywhere (your website, Discord, Twitter, GitHub README). Score auto-refreshes every 5 minutes.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">How to use it in 3 steps</div>
            <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
              <li>Click <span className="text-white">Register token</span>, paste your contract, pick the chain.</li>
              <li>Click the <span className="text-white">Embed</span> button on the token card — copy the HTML, Markdown, or BBCode snippet.</li>
              <li>Paste it on your website / README / Discord. The badge stays live and updates automatically.</li>
            </ol>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">How to read this:</span> The health score measures whether observable TRADING metrics look organic. It does NOT prove the project is legitimate, audited, or compliant. Use it alongside the Listing Readiness scorecard for a fuller view. A high score is a signal, not a certification.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
      {message}
    </div>
  );
}

// ─── Registration form ──────────────────────────────────────────────────────

function RegisterForm({ onSubmit, onCancel, loading }) {
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [network, setNetwork] = useState('ethereum');
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5 animate-fade-in">
      <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Register Token</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Display name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Your Token"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-pink-400/40 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Contract address</label>
          <input type="text" value={address} onChange={e => setAddress(e.target.value.trim())} placeholder="0x..."
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-pink-400/40 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1.5">Network</label>
          <select value={network} onChange={e => setNetwork(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-pink-400/40 focus:outline-none">
            {NETWORKS.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-xs text-gray-400 border border-dark-600 hover:text-white">Cancel</button>
        <button onClick={() => onSubmit({ tokenAddress: address, tokenName: name, network })}
          disabled={!address || !/^0x[a-fA-F0-9]{40}$/.test(address) || loading}
          className="px-4 py-2 rounded-lg text-xs font-bold bg-pink-500 text-white hover:bg-pink-400 disabled:opacity-40">
          {loading ? <Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 inline mr-1" />} Register
        </button>
      </div>
    </div>
  );
}

// ─── Token card (list view) ─────────────────────────────────────────────────

function TokenCard({ token, onSelect, onEmbed, onDelete }) {
  const rs = RATING_STYLES[token.rating] || RATING_STYLES.pending;
  const isPending = token.score == null;
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 hover:border-pink-500/30 transition p-5">
      <div className="flex items-center gap-4 mb-3">
        <ScoreRing value={token.score} rating={token.rating || 'pending'} size={64} />
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold truncate">{token.tokenName}</h3>
          <p className="text-[10px] text-gray-500 font-mono truncate">{token.tokenAddress}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rs.bg} ${rs.text} uppercase`}>
              {isPending ? 'PENDING' : token.rating}
            </span>
            <span className="text-[10px] text-gray-600 capitalize">{token.network}</span>
            {token.measuredFactors && (
              <span className="text-[10px] text-gray-500 font-mono">{token.measuredFactors.length}/6 factors</span>
            )}
          </div>
        </div>
      </div>
      {token.sources && (
        <div className="flex flex-wrap gap-1 mb-3">
          {token.sources.dexMetrics  && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">DEX METRICS ✓</span>}
          {token.sources.marketData  && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">MARKET DATA ✓</span>}
          {token.sources.onChain     && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30">ON-CHAIN ✓</span>}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => onSelect(token)}
          className="flex-1 py-1.5 rounded-lg text-[11px] bg-dark-700 text-gray-300 hover:bg-dark-600 flex items-center justify-center gap-1.5">
          <Eye className="w-3 h-3" /> Breakdown
        </button>
        <button onClick={() => onEmbed(token)}
          className="flex-1 py-1.5 rounded-lg text-[11px] bg-dark-700 text-gray-300 hover:bg-dark-600 flex items-center justify-center gap-1.5">
          <Code className="w-3 h-3" /> Embed
        </button>
        <button onClick={() => onDelete(token.tokenAddress)}
          className="py-1.5 px-2.5 rounded-lg text-[11px] text-gray-600 hover:text-red-400 hover:bg-red-500/10">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─── Embed modal ────────────────────────────────────────────────────────────

function EmbedModal({ token, onClose }) {
  const [copied, setCopied] = useState('');
  const chain = token.network || 'ethereum';
  const badgeUrl = `${window.location.origin}${API_BASE}/volume-health/badge/${chain}/${token.tokenAddress}`;
  const scoreUrl = `${window.location.origin}${API_BASE}/volume-health/score/${chain}/${token.tokenAddress}`;
  const codes = {
    html: `<a href="${window.location.origin}" target="_blank" rel="noopener">\n  <img src="${badgeUrl}" alt="haythix Health Score" />\n</a>`,
    markdown: `[![haythix Health Score](${badgeUrl})](${window.location.origin})`,
    bbcode: `[url=${window.location.origin}][img]${badgeUrl}[/img][/url]`,
    directUrl: badgeUrl,
    apiUrl: scoreUrl,
  };
  function copy(key) {
    navigator.clipboard.writeText(codes[key]);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-white">Embed your badge</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="bg-dark-900 rounded-xl p-4 flex items-center justify-center mb-6">
            <img src={`${API_BASE}/volume-health/badge/${chain}/${token.tokenAddress}`} alt="Badge" className="max-w-full" />
          </div>
          {[
            { key: 'html',      label: 'HTML' },
            { key: 'markdown',  label: 'Markdown (README)' },
            { key: 'bbcode',    label: 'BBCode (forums)' },
            { key: 'directUrl', label: 'Direct image URL' },
            { key: 'apiUrl',    label: 'JSON API endpoint' },
          ].map(({ key, label }) => (
            <div key={key} className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-gray-500 font-medium">{label}</label>
                <button onClick={() => copy(key)} className="text-xs text-pink-300 hover:text-pink-200 flex items-center gap-1">
                  {copied === key ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <pre className="bg-dark-900 rounded-lg p-3 text-[11px] text-gray-400 font-mono overflow-x-auto whitespace-pre-wrap break-all border border-dark-700/50">
                {codes[key]}
              </pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Detail / breakdown view ────────────────────────────────────────────────

function ScoreDetail({ token, score, onBack, onRefresh, refreshing }) {
  const rs = RATING_STYLES[score?.rating] || RATING_STYLES.pending;
  const breakdown = score?.breakdown || {};
  const isPending = score?.value == null;

  // Identify the strongest and weakest factors so we can write a plain-English
  // summary the user can read in 3 seconds instead of squinting at 6 cards.
  const measuredFactors = Object.entries(breakdown).filter(([, f]) => typeof f.score === 'number');
  const sortedByPct = [...measuredFactors].sort(([, a], [, b]) => (b.score / b.max) - (a.score / a.max));
  const strongest = sortedByPct[0]?.[1];
  const weakest = sortedByPct[sortedByPct.length - 1]?.[1];
  const sameFactor = sortedByPct.length < 2;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to tokens
        </button>
        <button onClick={onRefresh} disabled={refreshing}
          className="px-3 py-1.5 rounded-lg border border-pink-400/30 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 text-xs disabled:opacity-40 flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh score
        </button>
      </div>

      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-center gap-6">
          <ScoreRing value={score?.value} rating={score?.rating || 'pending'} size={96} />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white">{token.tokenName}</h2>
            <p className="text-xs text-gray-500 font-mono">{token.tokenAddress}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${rs.bg} ${rs.text} uppercase`}>
                {isPending ? 'PENDING' : score?.rating}
              </span>
              <span className="text-xs text-gray-500 capitalize">{token.network}</span>
              {score?.earned != null && score?.maxPossible > 0 && (
                <span className="text-xs text-gray-400 font-mono">{score.earned} / {score.maxPossible} pts</span>
              )}
              {score?.missingFactors?.length > 0 && (
                <span className="text-xs text-amber-400 font-mono">{score.missingFactors.length} factor{score.missingFactors.length === 1 ? '' : 's'} unmeasured</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* What this means — plain-English summary */}
      {!isPending && strongest && (
        <div className="bg-gradient-to-br from-pink-500/10 to-cyan-500/5 border border-pink-500/30 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-500/20 border border-pink-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-pink-300" />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="text-sm font-bold text-white">What this score means</h3>
              <p className="text-xs text-gray-300 leading-relaxed">
                {token.tokenName} scored <span className="text-white font-mono font-semibold">{score.value}/100</span> ({score.rating}). {measuredFactors.length} of 6 factors were measurable — {score.earned} of {score.maxPossible} points earned across those.
              </p>
              <p className="text-xs text-gray-300 leading-relaxed">
                <span className="text-emerald-300 font-semibold">Strongest:</span> <span className="text-white">{strongest.label}</span> at <span className="font-mono">{strongest.score}/{strongest.max}</span>{strongest.dataPoint ? ` (${strongest.dataPoint})` : ''}.
              </p>
              {!sameFactor && weakest && (
                <p className="text-xs text-gray-300 leading-relaxed">
                  <span className="text-amber-300 font-semibold">Weakest:</span> <span className="text-white">{weakest.label}</span> at <span className="font-mono">{weakest.score}/{weakest.max}</span>{weakest.dataPoint ? ` (${weakest.dataPoint})` : ''}. Improving this is the highest-leverage move for the overall score.
                </p>
              )}
              {score.missingFactors?.length > 0 && (
                <p className="text-xs text-gray-400 leading-relaxed">
                  Note: {score.missingFactors.length} factor{score.missingFactors.length === 1 ? ' was' : 's were'} not measurable for this token. The score reflects what we could verify, scaled to 100 — the unmeasured factors aren't penalized.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(breakdown).map(([key, factor]) => {
          const Icon = FACTOR_ICONS[key] || Shield;
          const hasScore = typeof factor.score === 'number';
          const pctOfMax = hasScore ? (factor.score / factor.max) * 100 : 0;
          return (
            <div key={key} className="bg-dark-800/60 rounded-xl border border-dark-700/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${hasScore ? 'text-pink-400' : 'text-gray-600'}`} />
                <span className="text-xs text-white font-semibold flex-1">{factor.label}</span>
                <SourceBadge source={factor.source} />
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className={`text-2xl font-bold font-mono ${hasScore ? 'text-white' : 'text-gray-500'}`}>
                  {hasScore ? factor.score : '—'}
                </span>
                <span className="text-xs text-gray-600">/ {factor.max}</span>
              </div>
              <div className="h-1.5 bg-dark-700 rounded-full mb-2 overflow-hidden">
                {hasScore && (
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${pctOfMax}%`,
                    backgroundColor: pctOfMax >= 80 ? '#10b981' : pctOfMax >= 60 ? '#3b82f6' : pctOfMax >= 40 ? '#f59e0b' : '#ef4444',
                  }} />
                )}
              </div>
              <p className="text-[11px] text-gray-400 leading-relaxed">{factor.dataPoint || factor.detail || ''}</p>
              <p className="text-[10px] text-gray-600 mt-1 leading-snug">{factor.desc}</p>
            </div>
          );
        })}
      </div>

      {score?.disclaimer && (
        <div className="bg-dark-900/40 border border-dark-700/40 rounded-xl p-4 text-[11px] text-gray-500 leading-relaxed">
          <Info className="w-3.5 h-3.5 inline mr-1 text-gray-500" /> {score.disclaimer}
        </div>
      )}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function VolumeHealthPage() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [embedToken, setEmbedToken] = useState(null);
  const [detailView, setDetailView] = useState(null);
  const [detailScore, setDetailScore] = useState(null);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);

  async function loadTokens() {
    setRefreshing(true);
    try {
      const data = await listHealthTokens();
      setTokens(data?.tokens || []);
    } catch { setTokens([]); }
    setLoading(false);
    setRefreshing(false);
  }
  useEffect(() => { loadTokens(); }, []);

  async function handleRegister(data) {
    setRegistering(true);
    try {
      await registerHealthToken(data.tokenAddress, data.tokenName, data.network);
      setToast({ message: 'Token registered — fetching live score…', type: 'success' });
      setShowRegister(false);
      loadTokens();
    } catch (err) {
      setToast({ message: `Registration failed: ${err.message}`, type: 'error' });
    }
    setRegistering(false);
  }
  async function handleDelete(addr) {
    if (!window.confirm('Remove this token?')) return;
    try {
      await deleteHealthToken(addr);
      setToast({ message: 'Token removed', type: 'success' });
      loadTokens();
    } catch (err) {
      setToast({ message: `Delete failed: ${err.message}`, type: 'error' });
    }
  }
  async function openDetail(token) {
    setDetailView(token);
    setDetailRefreshing(true);
    try {
      const score = await getHealthScore(token.tokenAddress, token.network);
      setDetailScore(score);
    } catch (err) {
      setDetailScore({ value: token.score, rating: token.rating, breakdown: {}, reason: err.message });
    } finally {
      setDetailRefreshing(false);
    }
  }
  async function refreshDetailScore() {
    if (!detailView) return;
    setDetailRefreshing(true);
    try {
      // forceRefresh bypasses the 5-minute server cache so the user can
      // override a stale/failed-fetch result by clicking Refresh.
      const score = await getHealthScore(detailView.tokenAddress, detailView.network, { forceRefresh: true });
      setDetailScore(score);
    } finally {
      setDetailRefreshing(false);
    }
  }

  if (detailView) {
    return (
      <FeatureGate feature="volumeHealthWidget">
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
          <Hero />
          <ScoreDetail
            token={detailView}
            score={detailScore}
            onBack={() => { setDetailView(null); setDetailScore(null); }}
            onRefresh={refreshDetailScore}
            refreshing={detailRefreshing}
          />
          {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
        </div>
      </FeatureGate>
    );
  }

  return (
    <FeatureGate feature="volumeHealthWidget">
      <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
        <Hero
          onRefresh={loadTokens}
          refreshing={refreshing}
          onRegister={() => setShowRegister(true)}
        />

        <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />

        {showRegister && (
          <RegisterForm onSubmit={handleRegister} onCancel={() => setShowRegister(false)} loading={registering} />
        )}

        {loading && (
          <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 p-10 text-center">
            <Loader2 className="w-6 h-6 text-pink-400 mx-auto animate-spin" />
          </div>
        )}

        {!loading && tokens.length === 0 && !showRegister && (
          <>
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 border-dashed p-10 text-center">
              <HeartPulse className="w-12 h-12 text-pink-400/40 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-1">No tokens registered yet</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
                Register your token to get an embeddable health badge. Score refreshes every 5 minutes automatically from real DEX activity, market data, and on-chain holder distribution.
              </p>
              <button onClick={() => setShowRegister(true)} className="px-5 py-2.5 rounded-xl bg-pink-500 text-white text-sm font-bold hover:bg-pink-400">
                <Plus className="w-4 h-4 inline mr-1" /> Register your first token
              </button>
            </div>

            {/* Try a sample — let users see a real health report before they
                bother registering, using a known mainstream token. */}
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-5">
              <div className="flex items-center gap-2 mb-2">
                <TestTube className="w-4 h-4 text-pink-400" />
                <h3 className="text-sm font-bold text-white">Want to see a real report first?</h3>
              </div>
              <p className="text-[11px] text-gray-500 mb-4">
                Score one of these well-known tokens — no registration needed. Gives you a feel for what the output looks like for an active, healthy token before you set up your own.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SAMPLE_TOKENS.map(s => (
                  <button
                    key={s.address}
                    onClick={() => openDetail({ tokenAddress: s.address, tokenName: `${s.name} (${s.symbol})`, network: s.network })}
                    className="text-left px-3 py-2.5 rounded-lg bg-dark-900/40 hover:bg-dark-700/40 border border-dark-700/50 hover:border-pink-500/30 transition"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-semibold text-white">{s.symbol}</div>
                      <div className="text-[9px] uppercase tracking-wider text-gray-500 font-mono">{s.category}</div>
                    </div>
                    <div className="text-[10px] text-gray-500 truncate mt-0.5">{s.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {!loading && tokens.length > 0 && (
          <>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{tokens.length} token{tokens.length !== 1 ? 's' : ''} registered · scores cached 5 min</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tokens.map(t => (
                <TokenCard key={t.tokenAddress + ':' + t.network}
                  token={t}
                  onSelect={openDetail}
                  onEmbed={setEmbedToken}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </>
        )}

        {embedToken && <EmbedModal token={embedToken} onClose={() => setEmbedToken(null)} />}
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      </div>
    </FeatureGate>
  );
}
